import type { ImageSizes } from '@fuuze/shared';
import Skeleton from 'react-loading-skeleton';

const FALLBACK_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23161616' width='200' height='200'/%3E%3Ctext fill='%23494949' font-family='sans-serif' font-size='14' font-weight='bold' text-anchor='middle' x='100' y='104'%3EFUUZE%3C/text%3E%3C/svg%3E";

type FuuzeImageProps = Omit<React.ComponentProps<'img'>, 'src' | 'width' | 'height' | 'alt' | 'loading' | 'srcSet'> & {
  imageSizes?: ImageSizes;
  size?: 'sm' | 'md' | 'lg';
  src?: string;
  isLoading?: boolean;
  width?: number;
  height?: number;
  alt: string;
  priority?: boolean;
  borderRadius?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
};

function resolveSrc(imageSizes?: ImageSizes, size?: string, src?: string): string {
  if (imageSizes && size) return imageSizes[size as keyof ImageSizes];
  return src ?? FALLBACK_SVG;
}

function buildSrcSet(imageSizes?: ImageSizes): string | undefined {
  if (!imageSizes) return undefined;
  return `${imageSizes.sm} 256w, ${imageSizes.md} 640w, ${imageSizes.lg} 1280w`;
}

export function FuuzeImage({
  imageSizes,
  size,
  src,
  isLoading = false,
  width,
  height,
  alt,
  priority = false,
  borderRadius = 8,
  objectFit = 'cover',
  className,
  style,
  onError,
  ...rest
}: FuuzeImageProps) {
  if (isLoading) {
    return <Skeleton width={width} height={height} borderRadius={borderRadius} className={className} style={style} />;
  }

  const resolvedSrc = resolveSrc(imageSizes, size, src);
  const srcSet = buildSrcSet(imageSizes);

  const imgStyle: React.CSSProperties = {
    borderRadius,
    objectFit,
    ...style,
  };

  return (
    <img
      src={resolvedSrc}
      srcSet={srcSet}
      sizes={srcSet ? `${width}px` : undefined}
      alt={alt}
      width={style?.width !== undefined || style?.height !== undefined ? undefined : width}
      height={style?.width !== undefined || style?.height !== undefined ? undefined : height}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      className={className}
      style={imgStyle}
      onError={e => {
        if (e.currentTarget.src !== FALLBACK_SVG) {
          e.currentTarget.removeAttribute('srcset');
          e.currentTarget.src = FALLBACK_SVG;
        }
        onError?.(e);
      }}
      {...rest}
    />
  );
}
